import { lazy, Suspense } from 'solid-js';
import { useVersionCheck } from './hooks/useVersionCheck';
import { Router, Route, Navigate, type RouteSectionProps } from '@solidjs/router';
import { MetaProvider } from '@solidjs/meta';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { SocketProvider } from './context/SocketContext';
import { ThemeProvider } from './context/ThemeContext';
import SeasonalEffect from './components/SeasonalEffect';
import Layout from './components/Layout';
import { AuthShell, AdminShell, SupportAdminShell } from './components/Layout';
import CookieBanner from './components/CookieBanner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import UpdateBanner from './components/UpdateBanner';
import LanguagePickerScreen from './components/LanguagePickerScreen';
import PlaceholderPage from './pages/PlaceholderPage';

const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Startseite = lazy(() => import('./pages/Startseite'));
const News = lazy(() => import('./pages/News'));
const NewsArticle = lazy(() => import('./pages/NewsArticle'));
const SpeciesIndex = lazy(() => import('./pages/SpeciesIndex'));
const SpeciesDetail = lazy(() => import('./pages/SpeciesDetail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ResetPasswordConfirm = lazy(() => import('./pages/ResetPasswordConfirm'));
const EmailChangeConfirm = lazy(() => import('./pages/EmailChangeConfirm'));
const Activate = lazy(() => import('./pages/Activate'));
const ActivateAccount = lazy(() => import('./pages/ActivateAccount'));
const Statistics = lazy(() => import('./pages/Statistics'));
const Status = lazy(() => import('./pages/Status'));
const EventLexikon = lazy(() => import('./pages/EventLexikon'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const AGB = lazy(() => import('./pages/AGB'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const CookieSettings = lazy(() => import('./pages/CookieSettings'));
const UnsubscribeSuccess = lazy(() => import('./pages/UnsubscribeSuccess'));
const UnsubscribeError = lazy(() => import('./pages/UnsubscribeError'));
const Home = lazy(() => import('./pages/Home'));
const Kontakt = lazy(() => import('./pages/Kontakt'));
const PublicLeaderboard = lazy(() => import('./pages/PublicLeaderboard'));
const Credits = lazy(() => import('./pages/Credits'));
const Links = lazy(() => import('./pages/Links'));
const Hilfe = lazy(() => import('./pages/Hilfe'));
const PWAInstall = lazy(() => import('./pages/PWAInstall'));
const Tutorial = lazy(() => import('./pages/Tutorial'));
const BugReport = lazy(() => import('./pages/BugReport'));
const ZooTierparkDonation = lazy(() => import('./pages/ZooTierparkDonation'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const DiscordSettings = lazy(() => import('./pages/DiscordSettings'));
const Customs = lazy(() => import('./pages/Customs'));
const Referral = lazy(() => import('./pages/Referral'));
const ShopAnmeldung = lazy(() => import('./pages/ShopAnmeldung'));
const PartnerShops = lazy(() => import('./pages/PartnerShops'));
const NestLayout = lazy(() => import('./pages/nest/NestLayout'));
const NestOverview = lazy(() => import('./pages/nest/NestOverview'));
const NestChamberPage = lazy(() => import('./pages/nest/NestChamberPage'));
const NestQuestPage = lazy(() => import('./pages/nest/NestQuestPage'));
const Profile = lazy(() => import('./pages/Profile'));
const Compare = lazy(() => import('./pages/Compare'));

const VacationMode = lazy(() => import('./pages/VacationMode'));
const WheelOfAnts = lazy(() => import('./pages/WheelOfAnts'));
const FoodSearch = lazy(() => import('./pages/FoodSearch'));
const QueenSearch = lazy(() => import('./pages/QueenSearch'));
const Regions = lazy(() => import('./pages/Regions'));
const Inventory = lazy(() => import('./pages/Inventory'));
const EventLog = lazy(() => import('./pages/EventLog'));
const AccountHistory = lazy(() => import('./pages/AccountHistory'));
const Friends = lazy(() => import('./pages/Friends'));
const Messages = lazy(() => import('./pages/Messages'));
const Housing = lazy(() => import('./pages/Housing'));
const Breeding = lazy(() => import('./pages/Breeding'));
const Support = lazy(() => import('./pages/Support'));
const Jobs = lazy(() => import('./pages/Jobs'));
const Lexicon = lazy(() => import('./pages/Lexicon'));
const Research = lazy(() => import('./pages/Research'));
const ShopAnkauf = lazy(() => import('./pages/ShopAnkauf'));
const ShopStart = lazy(() => import('./pages/ShopStart'));
const Marketplace = lazy(() => import('./pages/Marketplace'));
const RealEstate = lazy(() => import('./pages/RealEstate'));
const Trading = lazy(() => import('./pages/Trading'));
const Chat = lazy(() => import('./pages/Chat'));
const FurnitureShop = lazy(() => import('./pages/FurnitureShop'));
const ShopCategory = lazy(() => import('./pages/ShopCategory'));
const Furniture = lazy(() => import('./pages/Furniture'));
const WorldMap = lazy(() => import('./pages/WorldMap'));
const WorldSelect = lazy(() => import('./pages/WorldSelect'));
const AkAdminMap = lazy(() => import('./pages/AkAdminMap'));
const AkMessages = lazy(() => import('./pages/Messages'));
const AkProfile = lazy(() => import('./pages/AkProfile'));
const Hobby = lazy(() => import('./pages/Hobby'));
const Workshop = lazy(() => import('./pages/Workshop'));
const Ideas = lazy(() => import('./pages/Ideas'));
const Rooms = lazy(() => import('./pages/Rooms'));
const ColonyDetail = lazy(() => import('./pages/ColonyDetail'));
const SupportAdmin = lazy(() => import('./pages/SupportAdmin'));
const Admin = lazy(() => import('./pages/admin/Admin'));

/**
 * Wurzel-Layout des Routers. Solid Router 0.14 behandelt direkte Kinder von <Router>,
 * die keine <Route> sind, als leere Routen – CookieBanner und PWAInstallPrompt wurden
 * deshalb nie gerendert (nachgewiesen 23.10.2026: kein Cookie-Banner auf der Live-Seite).
 * Globale Overlays gehören daher hierher, in den `root`.
 */
const RootLayout = (props: RouteSectionProps) => (
  <>
    <Suspense fallback={<div class="layout-content" style={{ 'display': 'flex', 'justify-content': 'center', 'align-items': 'center', 'min-height': '50vh', 'color': '#888' }}>Lädt...</div>}>
      {props.children}
    </Suspense>
    <CookieBanner />
    <PWAInstallPrompt />
    <UpdateBanner />
    <SeasonalEffect />
  </>
);

function App() {
  useVersionCheck();

  return (
    <ThemeProvider>
    <AuthProvider>
      <LanguageProvider>
        <LanguagePickerScreen>
        <SocketProvider>
          <MetaProvider>
            <Router root={RootLayout}>

                {/* ── Öffentliche Seiten ohne Layout (Auth-Flows) ──────── */}
                <Route path="/login" component={Login} />
                <Route path="/register" component={Register} />
                <Route path="/reset-password" component={ResetPassword} />
                <Route path="/reset-password/:token" component={ResetPasswordConfirm} />
                <Route path="/confirm-email-change" component={EmailChangeConfirm} />
                <Route path="/activate" component={Activate} />
                <Route path="/activate/:token" component={ActivateAccount} />
                <Route path="/unsubscribe" component={Unsubscribe} />
                <Route path="/unsubscribe/success" component={UnsubscribeSuccess} />
                <Route path="/unsubscribe/error" component={UnsubscribeError} />
                <Route path="/pwa-install" component={PWAInstall} />
                <Route path="/terms" component={Terms} />
                <Route path="/privacy" component={Privacy} />

                {/* ── Öffentliche Seiten mit Footer ─────────────────────── */}
                <Route path="/" component={() => <Layout public><Startseite /></Layout>} />
                <Route path="/hilfe" component={() => <Layout public><Hilfe /></Layout>} />
                <Route path="/impressum" component={() => <Layout public><Impressum /></Layout>} />
                <Route path="/datenschutz" component={() => <Layout public><Datenschutz /></Layout>} />
                <Route path="/agb" component={() => <Layout public><AGB /></Layout>} />
                <Route path="/cookie-settings" component={() => <Layout public><CookieSettings /></Layout>} />
                <Route path="/home" component={() => <Layout public><Home /></Layout>} />
                <Route path="/kontakt" component={() => <Layout public><Kontakt /></Layout>} />
                <Route path="/links" component={() => <Layout public><Links /></Layout>} />
                <Route path="/rangliste" component={() => <Layout public><PublicLeaderboard /></Layout>} />
                <Route path="/bug-report" component={() => <Layout public><BugReport /></Layout>} />
                <Route path="/arten" component={() => <Layout public><SpeciesIndex /></Layout>} />
                <Route path="/arten/:slug" component={() => <Layout public><SpeciesDetail /></Layout>} />
                <Route path="/status" component={() => <Layout public><Status /></Layout>} />
                <Route path="/news" component={() => <Layout public><News /></Layout>} />
                <Route path="/news/:id" component={() => <Layout public><NewsArticle /></Layout>} />

                {/* ── Eingeloggte Seiten – Navigation bleibt persistent ── */}
                <Route component={AuthShell}>
                  <Route path="/dashboard" component={Dashboard} />
                  <Route path="/startseite" component={Startseite} />
                  <Route path="/statistics" component={Statistics} />
                  <Route path="/event-lexikon" component={EventLexikon} />
                  <Route path="/credits" component={Credits} />
                  <Route path="/tutorial" component={Tutorial} />
                  <Route path="/zoo-tierpark/donation" component={ZooTierparkDonation} />
                  {/* Platzhalterseiten umgeleitet: Zoo → Spenden-Seite, Universität → Forschungslabor */}
                  <Route path="/zoo-tierpark" component={() => <Navigate href="/zoo-tierpark/donation" />} />
                  <Route path="/donations" component={() => <Navigate href="/dashboard" />} />
                  <Route path="/university" component={() => <Navigate href="/research" />} />
                  <Route path="/notification-settings" component={NotificationSettings} />
                  <Route path="/discord-settings" component={DiscordSettings} />
                  <Route path="/customs" component={Customs} />
                  <Route path="/shop-anmeldung" component={ShopAnmeldung} />
                  <Route path="/partner-shops" component={PartnerShops} />
                  <Route path="/profile/:userId" component={Profile} />
                  <Route path="/profile" component={Profile} />
                  <Route path="/compare/:userId" component={Compare} />
                  {/* M1: Kolonie-Erstellung ist im Backend admin-only → Spieler landen im Zimmer statt auf einer 403-Seite */}
                  <Route path="/create-colony" component={() => <Navigate href="/rooms" />} />

                  <Route path="/vacation-mode" component={VacationMode} />
                  <Route path="/ant-kingdom" component={() => <Navigate href="/nest" />} />
                  <Route path="/nest" component={NestLayout}>
                    <Route path="/" component={NestOverview} />
                    <Route path="/quests" component={NestQuestPage} />
                    <Route path="/:key" component={NestChamberPage} />
                  </Route>
                  {/* /wheel nutzte falsche Feldnamen → auf die funktionierende Seite umleiten */}
                  <Route path="/wheel" component={() => <Navigate href="/wheel-of-ants" />} />
                  <Route path="/wheel-of-ants" component={WheelOfAnts} />
                  <Route path="/food-search" component={FoodSearch} />
                  <Route path="/queen-search" component={QueenSearch} />
                  <Route path="/regions" component={Regions} />
                  <Route path="/inventory" component={Inventory} />
                  <Route path="/event-log" component={EventLog} />
                  <Route path="/account-history" component={AccountHistory} />
                  <Route path="/referral" component={Referral} />
                  <Route path="/friends" component={Friends} />
                  <Route path="/messages" component={Messages} />
                  <Route path="/housing" component={Housing} />
                  <Route path="/breeding" component={Breeding} />
                  <Route path="/support" component={Support} />
                  <Route path="/jobs" component={Jobs} />
                  <Route path="/lexicon" component={Lexicon} />
                  <Route path="/lexicon/:category" component={Lexicon} />
                  <Route path="/research" component={Research} />
                  <Route path="/colonies" component={() => <Navigate href="/rooms" />} />
                  <Route path="/shop" component={ShopStart} />
                  <Route path="/shop/ankauf" component={ShopAnkauf} />
                  <Route path="/marketplace" component={Marketplace} />
                  <Route path="/real-estate" component={RealEstate} />
                  <Route path="/trading" component={Trading} />
                  <Route path="/chat" component={Chat} />
                  <Route path="/furniture-shop" component={FurnitureShop} />
                  <Route path="/shop/:category" component={ShopCategory} />
                  <Route path="/furniture" component={Furniture} />
                  {/* Allianzen: Datenbank-Tabellen fehlen → bis zur Fertigstellung ausgeblendet */}
                  <Route path="/alliances" component={() => <Navigate href="/dashboard" />} />
                  <Route path="/world-select" component={WorldSelect} />
                  <Route path="/world-map" component={WorldMap} />
                  <Route path="/admin/world-map" component={AkAdminMap} />
                  <Route path="/ak-messages" component={AkMessages} />
                  <Route path="/ak-profile/:userId" component={AkProfile} />
                  <Route path="/hobby" component={Hobby} />
                  <Route path="/workshop" component={Workshop} />
                  <Route path="/ideas" component={Ideas} />
                  <Route path="/rooms" component={Rooms} />
                  <Route path="/colonies/:id" component={ColonyDetail} />
                  <Route path="*" component={PlaceholderPage} />
                </Route>

                {/* ── Admin-Seiten ──────────────────────────────────────── */}
                <Route component={AdminShell}>
                  <Route path="/admin" component={Admin} />
                </Route>

                {/* ── Support-Admin-Seiten ──────────────────────────────── */}
                <Route component={SupportAdminShell}>
                  <Route path="/support-admin" component={SupportAdmin} />
                </Route>

            </Router>
          </MetaProvider>
        </SocketProvider>
        </LanguagePickerScreen>
      </LanguageProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
